/**
 * Чистая логика карточки сделки в переписке: из ответа CRM — в то, что рисует полоса и окно сделки.
 * Ни сети, ни React — проверяется отдельно (__tests__/dealCard.test.ts).
 */
import type { CardDeal, CardPerson, CardResponse, CardStage, FeedItem } from '@/api/crmCard';

export const FALLBACK_STAGE_COLOR = '#0abab5';

const CURRENCY_SIGN: Record<string, string> = { USD: '$', EUR: '€', RUB: '₽', UZS: 'сум', KZT: '₸' };

/** «1 250 000 $»; пусто или ноль — «—» (как в прототипе). */
export function formatMoney(amount: string | number | null | undefined, currency?: string | null): string {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (n === null || n === undefined || !Number.isFinite(n) || n === 0) return '—';
  const cur = (currency || 'USD').toUpperCase();
  return `${n.toLocaleString('ru-RU').replace(/\s/g, ' ')} ${CURRENCY_SIGN[cur] ?? cur}`;
}

/** Имя человека из карточки. */
export function personName(p: CardPerson | null | undefined): string {
  if (!p) return '';
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
}

export type StripModel =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  /** Переписка из другого проекта: карточку не показываем (CRM так и ответила). */
  | { kind: 'other_project'; message: string }
  | { kind: 'none' }
  | {
      kind: 'deal';
      dealId: string;
      title: string;
      stage: { id: string; name: string; color: string };
      amountText: string;
      lead: boolean;
    };

export function stageColor(s: Pick<CardStage, 'color'> | null | undefined): string {
  return s?.color || FALLBACK_STAGE_COLOR;
}

/** Ответ CRM → то, что показывает полоса сделки над перепиской. */
export function stripModel(card: CardResponse | undefined, state: { loading: boolean; error: string | null }): StripModel {
  if (!card) {
    if (state.error) return { kind: 'error', message: state.error };
    return { kind: 'loading' };
  }
  if (card.reason === 'other_project') {
    return { kind: 'other_project', message: card.message || 'Эта переписка относится к другому проекту' };
  }
  const deal = card.deal;
  if (!card.found || !deal) return { kind: 'none' };
  const stage = deal.stage ?? card.stages?.find((s) => s.id === deal.stageId) ?? null;
  return {
    kind: 'deal',
    dealId: deal.id,
    title: deal.company?.name || personName(deal.person ?? card.person) || deal.title,
    stage: { id: deal.stageId, name: stage?.name ?? 'Стадия', color: stageColor(stage) },
    amountText: formatMoney(deal.amount, deal.currency),
    lead: stage?.stageType === 'lead',
  };
}

/** Полоса стадий воронки: каждая стадия — отрезок одинаковой ширины; закрашены все до текущей включительно. */
export function trackModel(stages: CardStage[], currentId: string): Array<{ id: string; filled: boolean }> {
  const sorted = [...stages].sort((a, b) => a.position - b.position);
  const at = sorted.findIndex((s) => s.id === currentId);
  return sorted.map((s, i) => ({ id: s.id, filled: at >= 0 && i <= at }));
}

/** Стадии воронки для выбора: «успешные» и обычные по порядку, этапы отказа — отдельно (они спрашивают причину). */
export function splitStages(stages: CardStage[]): { regular: CardStage[]; lost: CardStage[] } {
  const sorted = [...stages].sort((a, b) => a.position - b.position);
  return { regular: sorted.filter((s) => s.stageType !== 'lost'), lost: sorted.filter((s) => s.stageType === 'lost') };
}

export type StageRoute = 'chatwoot' | 'crm';

/**
 * Каким путём менять стадию.
 *  • сделка привязана к ЭТОЙ переписке (её номер совпал или у переписки уже стоит стадия/сделка CRM) —
 *    через Chatwoot: он спросит CRM и сам запишет стадию у переписки;
 *  • иначе сделка найдена по человеку (по телефону/почте) — меняем в CRM напрямую, это та же дверь,
 *    что у веб-карточки; стадию на переписку CRM отразит сама.
 */
export function stageRoute(deal: CardDeal, displayId: number, conv: { crmStageId?: string | null; crmDealId?: string | null }): StageRoute {
  // Только видимый номер: внутренний (chatwootConversationId) с видимым сравнивать нельзя — совпадение случайно.
  const linkedByNumber = deal.chatwootDisplayId === displayId;
  const linkedByAttrs = conv.crmDealId === deal.id;
  return linkedByNumber || linkedByAttrs ? 'chatwoot' : 'crm';
}

/** Какой значок рисовать у строки ленты касаний. */
export type FeedIcon = 'chat' | 'call' | 'call-missed' | 'note' | 'task' | 'stage' | 'doc' | 'touch' | 'other';

export function feedIcon(item: Pick<FeedItem, 'group' | 'kind' | 'title'>): FeedIcon {
  if (item.group === 'call') {
    return /пропущен|не состоял|no_answer|не ответ/i.test(`${item.title} ${item.kind}`) ? 'call-missed' : 'call';
  }
  switch (item.group) {
    case 'chat': return 'chat';
    case 'note': return 'note';
    case 'task': return 'task';
    case 'stage': return 'stage';
    case 'doc': return 'doc';
    case 'touch': return 'touch';
    default: return 'other';
  }
}

/** Последние касания: свежие первыми, не больше `limit`. */
export function latestFeed(items: FeedItem[], limit = 20): FeedItem[] {
  return [...items]
    .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt))
    .slice(0, limit);
}

/** Телефон для звонка: сперва из переписки, потом из карточки CRM. */
export function phoneFor(convPhone: string | null | undefined, card: CardResponse | undefined): string | null {
  const fromCard = card?.deal?.person?.phones?.[0] ?? card?.person?.phones?.[0] ?? null;
  const p = (convPhone || fromCard || '').trim();
  return p || null;
}

/** День ГГГГ-ММ-ДД по местному времени. */
export function localDayOf(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/** «сегодня» / «завтра» / «вчера» / «12 окт» для срока задачи. */
export function dueLabel(dueDate: string, now: Date = new Date()): { text: string; late: boolean } {
  const [y, m, d] = dueDate.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return { text: dueDate, late: false };
  const day = new Date(y, m - 1, d).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const diff = Math.round((day - today) / 86_400_000);
  const text = diff === 0 ? 'сегодня' : diff === 1 ? 'завтра' : diff === -1 ? 'вчера' : `${d} ${MONTHS[m - 1]}`;
  return { text, late: diff < 0 };
}

/** Момент «день + время ЧЧ:ММ» по местному времени → ISO для startsAt. */
export function startsAtOf(day: string, time: string): string | null {
  if (!/^\d{2}:\d{2}$/.test(time)) return null;
  const [y, m, d] = day.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const dt = new Date(y, m - 1, d, h, mi, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
}
